import { Injectable, signal, inject, computed, effect, Injector } from '@angular/core';
import { DatabaseService } from './db.service';
import { UserRole, Municipality } from '../models';
import { supabase } from '../supabase';
import { SessionSecurityService } from './session-security.service';

@Injectable({
    providedIn: 'root'
})
export class ContextService {
    private db = inject(DatabaseService);
    private injector = inject(Injector);

    private get sessionSecurity(): SessionSecurityService {
        return this.injector.get(SessionSecurityService);
    }

    // Current Session State
    currentUserRole = signal<UserRole | null>(null);
    currentMunicipalityId = signal<string | null>(null);
    currentOperator = signal<any | null>(null); // any for now, matches Operator from DB
    currentPatientCpf = signal<string | null>(null); // To auto-load patient details
    currentPatientBirthDate = signal<string | null>(null); // To auto-load patient details
    isPublicStandalone = signal(false);
    isInSelectionMode = signal(false);
    isRestoringSession = signal(true); // Default to true so UI waits for the check
    isModalOpen = signal(false); // Track if any modal is active for performance optimizations
    isUserTyping = signal(false); // Flag to prevent intrusive reloads

    // List managed by Admin
    availableMunicipalities = signal<Municipality[]>([]);

    currentMunicipality = computed(() => {
        const id = this.currentMunicipalityId();
        if (!id) return null;
        return this.availableMunicipalities().find(m => String(m.id).trim().toUpperCase() === String(id).trim().toUpperCase()) || null;
    });

    constructor() {
        effect(() => {
            this.db.isPublicMode.set(this.isPublicStandalone());
        });
    }

    async loadMunicipalities() {
        try {
            const muns = await this.db.getMunicipalities();
            console.log('[ContextService] Raw Municipalities:', muns.map(m => m.id));
            this.availableMunicipalities.set(muns); // Do not filter out Admin Central anymore
        } catch (err) {
            console.error('Erro ao carregar municípios:', err);
        }
    }

    async login(role: UserRole, municipalityId: string | null, operator: any = null, waitForData = false) {
        console.log('[ContextService] Login:', role, municipalityId);

        // Update both signals for reliability
        this.currentOperator.set(operator);
        this.db.currentOperator.set(operator);
        this.db.currentUserRole.set(role);

        // Ensure municipalities are loaded for Admin/Validation
        if (this.availableMunicipalities().length === 0) {
            await this.loadMunicipalities();
        }

        if (role === 'ADMIN_GERAL') {
            this.currentUserRole.set(role);
            this.currentMunicipalityId.set(municipalityId || 'GLOBAL');
            this.isInSelectionMode.set(true); // Always starts in selection for ADMIN_GERAL
            await this.db.initContext(municipalityId ? String(municipalityId) : 'GLOBAL');

            if (waitForData) {
                await this.db.loadBusinessData(municipalityId ? String(municipalityId) : null);
            } else {
                // Non-blocking load of heavy data
                this.db.loadBusinessData(municipalityId ? String(municipalityId) : null);
            }
        } else {
            const normalizedMunicipalityId = String(municipalityId).trim().toUpperCase();
            console.log('[ContextService] Looking for municipality with ID:', municipalityId, '| Normalized:', normalizedMunicipalityId);
            console.log('[ContextService] Available municipalities:', this.availableMunicipalities().map(m => ({ id: m.id, name: m.name })));

            const mun = this.availableMunicipalities().find(m => String(m.id).trim().toUpperCase() === normalizedMunicipalityId);

            if (!mun) {
                // CRITICAL FIX: Throw error instead of silently failing
                console.error('[ContextService] Municipality not found! ID:', municipalityId, '| Available:', this.availableMunicipalities().map(m => m.id));
                throw new Error(`Município não encontrado: ${municipalityId}. Entre em contato com o administrador.`);
            }

            if (!mun.isActive) {
                throw new Error('Acesso desativado para este município.');
            }
            if (operator && !operator.isActive) {
                throw new Error('Acesso do operador desativado.');
            }

            this.currentMunicipalityId.set(mun.id);
            this.currentUserRole.set(role);

            await this.db.initContext(String(mun.id));

            // PACIENTE não precisa do loadBusinessData (ele busca seus próprios dados de forma segura na view local)
            if (role !== 'PACIENTE') {
                if (waitForData) {
                    await this.db.loadBusinessData(String(mun.id));
                } else {
                    // Non-blocking load of heavy data
                    this.db.loadBusinessData(String(mun.id));
                }
            }

            console.log('[ContextService] Login successful! Role:', role, '| Municipality:', mun.name);
        }

        // Ativa monitoramento de inatividade (15 minutos) para operadores e administradores
        this.sessionSecurity.startInactivityMonitoring();
    }

    logout() {
        // Para o monitoramento de inatividade
        this.sessionSecurity.stopInactivityMonitoring();

        this.currentUserRole.set(null);
        this.currentMunicipalityId.set(null);
        this.currentOperator.set(null);
        this.db.currentOperator.set(null);
        this.db.currentUserRole.set(null);
        this.isPublicStandalone.set(false);
        this.db.isPublicMode.set(false);
        this.isInSelectionMode.set(false);

        // Limpa a sessão do Supabase ao sair
        supabase.auth.signOut();
    }

    /**
     * Tenta restaurar a sessão do usuário caso exista no Supabase.
     * Chamado no boot da aplicação (AppComponent).
     */
    async restoreSession() {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session || !session.user) return;

            console.log('[ContextService] Restaurando sessão para:', session.user.email);

            const res = await this.db.getOperatorByAuthId(session.user.id);
            if (res && res.operator) {
                // Restore state using login logic but without re-authenticating
                // The role shouldn't be hardcoded to res.operator.role string blindly if it needs mapping,
                // but login() expects 'ADMIN_GERAL', 'ADMIN_MUNICIPAL', etc.
                await this.login(res.operator.role, res.municipality.id, res.operator, false);
                console.log('[ContextService] Sessão restaurada com sucesso!');
            } else {
                // No valid operator found in DB for this auth user, sign out to be safe
                supabase.auth.signOut();
            }
        } catch (err) {
            console.error('[ContextService] Erro ao restaurar sessão:', err);
        } finally {
            this.isRestoringSession.set(false);
        }
    }

    async backToSelection() {
        if (this.currentUserRole() !== 'ADMIN_GERAL') return;

        console.log('[ContextService] Voltando para seleção...');
        this.currentMunicipalityId.set('GLOBAL');
        this.isInSelectionMode.set(true);
        await this.db.initContext('GLOBAL');
    }

    getNormalizedMunicipalityName(): string | null {
        const mun = this.currentMunicipality();
        if (!mun) return null;
        return mun.name
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove accents
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-'); // slugwise
    }
}
